import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Drawer,
  Divider,
  IconButton,
} from '@mui/material';
import {
  SportsCricket,
  LocationOn,
  Schedule as ScheduleIcon,
  Groups,
  Close,
} from '@mui/icons-material';
import type { ScheduleMatch, PlayerPoolEntry } from '../../types/database';
import { groupMatchesByDate, formatMatchDetails } from '../../utils/scheduleParser';
import colors from '../../theme/colors';
import { alpha } from '@mui/material';
import { getTeamColors } from '../../utils/teamLogos';
import { cricketFlagEmoji } from '../../utils/countryFlags';

interface MatchScheduleViewerProps {
  matches: ScheduleMatch[];
  highlightMatchNumber?: number; // Highlight a specific match (e.g., selected powerplay match)
  tournamentName?: string;
  playerPool?: PlayerPoolEntry[]; // Optional — enables squad bottom sheet on match tap
}

const cardSx = {
  background: `linear-gradient(145deg, ${alpha(colors.blue.navy, 0.95)} 0%, ${alpha('#0A1929', 0.98)} 100%)`,
  border: `1px solid ${colors.border.default}`,
  borderRadius: 3,
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  position: 'relative' as const,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${alpha(colors.blue.electric, 0.6)}, transparent)`,
  },
};

const ROLE_META: Record<string, { label: string; color: string }> = {
  batsman:      { label: 'BAT', color: '#F59E0B' },
  bowler:       { label: 'BWL', color: '#60A5FA' },
  allrounder:   { label: 'AR',  color: '#34D399' },
  wicketkeeper: { label: 'WK',  color: '#C084FC' },
};
const getRoleMeta = (role?: string) =>
  ROLE_META[role ?? ''] ?? { label: (role ?? '???').slice(0, 3).toUpperCase(), color: '#94a3b8' };

const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const MatchScheduleViewer: React.FC<MatchScheduleViewerProps> = ({
  matches,
  highlightMatchNumber,
  tournamentName,
  playerPool,
}) => {
  const groupedMatches = useMemo(() => groupMatchesByDate(matches), [matches]);
  const [selectedMatch, setSelectedMatch] = useState<ScheduleMatch | null>(null);

  const renderMatchCard = (match: ScheduleMatch) => {
    const details = formatMatchDetails(match);
    const isHighlighted = match.matchNumber === highlightMatchNumber;
    const isTBC = match.team1 === 'TBC' || match.team2 === 'TBC';

    const isIPL = tournamentName?.toLowerCase().includes('ipl');
    const t1Colors = isIPL && !isTBC ? getTeamColors(match.team1) : null;
    const t2Colors = isIPL && !isTBC ? getTeamColors(match.team2) : null;
    const teamGradientBg = t1Colors && t2Colors
      ? `linear-gradient(90deg,
          ${alpha(t1Colors[0], 0.38)} 0%,
          ${alpha(t1Colors[0], 0.18)} 28%,
          ${alpha('#0A1929', 0.92)} 42%,
          ${alpha('#0A1929', 0.92)} 58%,
          ${alpha(t2Colors[0], 0.18)} 72%,
          ${alpha(t2Colors[0], 0.38)} 100%)`
      : null;

    const highlightedCardSx = isHighlighted
      ? {
          ...cardSx,
          border: `1px solid ${colors.border.strong}`,
          boxShadow: `0 8px 32px rgba(0,0,0,0.3), ${colors.shadows.blue.md}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: `linear-gradient(90deg, transparent, ${alpha(colors.blue.electric, 0.8)}, transparent)`,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '3px',
            height: '100%',
            background: `linear-gradient(180deg, ${colors.orange.primary}, ${colors.orange.dark})`,
            borderRadius: '3px 0 0 3px',
          },
        }
      : cardSx;

    // Split teams for styled "vs" layout
    const teamParts = details.teams.split(' vs ');
    const team1 = teamParts[0] ?? details.teams;
    const team2 = teamParts[1] ?? null;

    return (
      <Card
        key={match.matchNumber}
        onClick={playerPool ? () => setSelectedMatch(match) : undefined}
        sx={{
          mb: 1.25,
          cursor: playerPool ? 'pointer' : 'default',
          ...highlightedCardSx,
          ...(teamGradientBg && { background: teamGradientBg }),
          ...(t1Colors && t2Colors && {
            border: `1px solid ${alpha('#ffffff', 0.1)}`,
            boxShadow: `0 4px 20px rgba(0,0,0,0.4),
              -3px 0 14px ${alpha(t1Colors[0], 0.18)},
              3px 0 14px ${alpha(t2Colors[0], 0.18)}`,
          }),
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: playerPool ? 'translateY(-1px)' : 'none',
            ...(t1Colors && t2Colors
              ? {
                  boxShadow: `0 10px 32px rgba(0,0,0,0.5),
                    -5px 0 22px ${alpha(t1Colors[0], 0.3)},
                    5px 0 22px ${alpha(t2Colors[0], 0.3)}`,
                  border: `1px solid ${alpha('#ffffff', 0.16)}`,
                }
              : {
                  boxShadow: isHighlighted
                    ? `0 8px 28px rgba(0,0,0,0.4), ${colors.shadows.blue.lg}`
                    : `0 8px 28px rgba(0,0,0,0.4)`,
                  border: isHighlighted
                    ? `1px solid ${colors.border.strong}`
                    : `1px solid ${alpha(colors.blue.electric, 0.4)}`,
                }),
          },
        }}
      >
        <CardContent sx={{ p: '10px 14px !important', pl: isHighlighted ? '16px !important' : '14px !important' }}>
          {/* Row 1: match badge + title + chips + squads cue */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Chip
                label={`M${match.matchNumber}`}
                size="small"
                sx={{
                  bgcolor: alpha(colors.blue.electric, 0.12),
                  color: colors.blue.light,
                  fontFamily: "'Bebas Neue', 'Satoshi', cursive",
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: 18,
                  flexShrink: 0,
                  border: `1px solid ${alpha(colors.blue.electric, 0.22)}`,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Typography
                fontWeight={600}
                fontSize="0.8rem"
                noWrap
                sx={{ color: alpha(colors.text.secondary, 0.75), letterSpacing: '0.01em' }}
              >
                {details.title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
              {isHighlighted && (
                <Chip
                  label="PP"
                  size="small"
                  sx={{
                    bgcolor: alpha(colors.orange.primary, 0.15),
                    color: colors.orange.light,
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: 18,
                    border: `1px solid ${alpha(colors.orange.primary, 0.4)}`,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
              {match.stage && (
                <Chip
                  label={match.stage}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(168,85,247,0.15)',
                    color: '#d8b4fe',
                    border: '1px solid rgba(168,85,247,0.4)',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    height: 18,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              )}
              {/* View squads cue */}
              {playerPool && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.35,
                    px: 0.75,
                    py: '2px',
                    borderRadius: 1,
                    bgcolor: alpha(colors.blue.electric, 0.08),
                    border: `1px solid ${alpha(colors.blue.electric, 0.2)}`,
                  }}
                >
                  <Groups sx={{ fontSize: 11, color: alpha(colors.blue.electric, 0.7) }} />
                  <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: alpha(colors.blue.electric, 0.7), letterSpacing: '0.02em' }}>
                    Squads
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Row 2: Teams */}
          {team2 && t1Colors && t2Colors ? (
            /* ── IPL Arena Clash Layout ── */
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'stretch',
                minHeight: 56,
                borderRadius: 1.5,
                overflow: 'hidden',
                border: `1.5px solid transparent`,
                backgroundClip: 'padding-box',
                boxShadow: `inset 0 0 0 1.5px ${alpha('#ffffff', 0.07)},
                  -3px 0 10px ${alpha(t1Colors[0], 0.22)},
                  3px 0 10px ${alpha(t2Colors[0], 0.22)}`,
                mb: 0.75,
              }}
            >
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 2, py: 1, background: `linear-gradient(110deg, ${alpha(t1Colors[0], 0.52)} 0%, ${alpha(t1Colors[0], 0.28)} 55%, ${alpha(t1Colors[0], 0.06)} 100%)`, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: `linear-gradient(90deg, ${t1Colors[0]} 0%, ${alpha(t1Colors[0], 0.2)} 100%)` }, '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, width: '70%', height: '1px', background: `linear-gradient(90deg, ${alpha(t1Colors[0], 0.6)}, transparent)` } }}>
                <Typography fontWeight={900} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: '#fff', textShadow: `0 0 20px ${alpha(t1Colors[0], 0.9)}, 0 2px 6px rgba(0,0,0,0.8)`, letterSpacing: '-0.01em', lineHeight: 1.2, maxWidth: '88%' }}>{cricketFlagEmoji(team1) && <Box component="span" sx={{ mr: 0.5 }}>{cricketFlagEmoji(team1)}</Box>}{team1}</Typography>
              </Box>
              <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '90px', transform: 'translateX(-50%)', zIndex: 2, background: `linear-gradient(90deg, transparent 0%, ${alpha(t1Colors[0], 0.18)} 20%, ${alpha(t1Colors[0], 0.28)} 42%, ${alpha(t2Colors[0], 0.28)} 58%, ${alpha(t2Colors[0], 0.18)} 80%, transparent 100%)` }} />
              <Box sx={{ position: 'absolute', left: '50%', top: '-10%', bottom: '-10%', width: '22px', transform: 'translateX(-50%) skewX(-12deg)', zIndex: 3, background: `linear-gradient(180deg, ${alpha(t1Colors[0], 0.5)} 0%, ${alpha(t1Colors[0], 0.3)} 30%, ${alpha(t2Colors[0], 0.3)} 70%, ${alpha(t2Colors[0], 0.5)} 100%)`, backdropFilter: 'blur(2px)' }} />
              <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', transform: 'translateX(-50%)', zIndex: 4, background: `linear-gradient(180deg, transparent 0%, ${t1Colors[0]} 15%, #ffffff 48%, #ffffff 52%, ${t2Colors[0]} 85%, transparent 100%)`, boxShadow: `0 0 6px 1px ${alpha(t1Colors[0], 0.5)}, 0 0 6px 1px ${alpha(t2Colors[0], 0.5)}` }} />
              <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 20, width: 42, height: 42, clipPath: 'polygon(8% 0%, 92% 0%, 100% 20%, 100% 65%, 50% 100%, 0% 65%, 0% 20%)', background: `linear-gradient(170deg, ${t1Colors[0]} 0%, ${alpha(t1Colors[0], 0.85)} 30%, #071420 48%, ${alpha(t2Colors[0], 0.85)} 70%, ${t2Colors[0]} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', '&::before': { content: '""', position: 'absolute', inset: '2px', clipPath: 'polygon(8% 0%, 92% 0%, 100% 20%, 100% 65%, 50% 100%, 0% 65%, 0% 20%)', background: `linear-gradient(170deg, ${alpha('#ffffff', 0.15)} 0%, transparent 60%)` } }}>
                <Typography sx={{ fontFamily: "'Bebas Neue', 'Impact', 'Arial Black', sans-serif", fontWeight: 900, fontSize: '0.9rem', letterSpacing: '0.1em', color: '#fff', lineHeight: 1, userSelect: 'none', position: 'relative', zIndex: 1, textShadow: '0 0 10px rgba(255,255,255,0.6), 0 2px 5px rgba(0,0,0,0.95)', mt: '-3px' }}>VS</Typography>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 2, py: 1, background: `linear-gradient(250deg, ${alpha(t2Colors[0], 0.52)} 0%, ${alpha(t2Colors[0], 0.28)} 55%, ${alpha(t2Colors[0], 0.06)} 100%)`, position: 'relative', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: `linear-gradient(270deg, ${t2Colors[0]} 0%, ${alpha(t2Colors[0], 0.2)} 100%)` }, '&::after': { content: '""', position: 'absolute', bottom: 0, right: 0, width: '70%', height: '1px', background: `linear-gradient(270deg, ${alpha(t2Colors[0], 0.6)}, transparent)` } }}>
                <Typography fontWeight={900} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: '#fff', textShadow: `0 0 20px ${alpha(t2Colors[0], 0.9)}, 0 2px 6px rgba(0,0,0,0.8)`, letterSpacing: '-0.01em', lineHeight: 1.2, textAlign: 'right', maxWidth: '88%' }}>{team2}{cricketFlagEmoji(team2) && <Box component="span" sx={{ ml: 0.5 }}>{cricketFlagEmoji(team2)}</Box>}</Typography>
              </Box>
            </Box>
          ) : (
            /* ── Default vs layout ── */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
              <Typography fontWeight={800} fontSize={{ xs: '0.9rem', sm: '1rem' }} sx={{ color: isTBC ? alpha(colors.text.secondary, 0.35) : colors.text.primary, fontStyle: isTBC ? 'italic' : 'normal' }}>
                {cricketFlagEmoji(team1) && <Box component="span" sx={{ mr: 0.5 }}>{cricketFlagEmoji(team1)}</Box>}{team1}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha(colors.text.secondary, 0.4), fontWeight: 500, fontSize: '0.75rem', flexShrink: 0 }}>vs</Typography>
              {team2 && (
                <Typography fontWeight={800} fontSize={{ xs: '0.9rem', sm: '1rem' }} sx={{ color: isTBC ? alpha(colors.text.secondary, 0.35) : colors.text.primary, fontStyle: isTBC ? 'italic' : 'normal' }}>
                  {cricketFlagEmoji(team2) && <Box component="span" sx={{ mr: 0.5 }}>{cricketFlagEmoji(team2)}</Box>}{team2}
                </Typography>
              )}
            </Box>
          )}

          {/* Row 3: Venue + time on one line */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <LocationOn sx={{ fontSize: 12, color: alpha(colors.blue.electric, 0.5) }} />
              <Typography sx={{ fontSize: '0.7rem', color: alpha(colors.text.secondary, 0.55), lineHeight: 1 }} noWrap>
                {details.venue}
              </Typography>
            </Box>
            <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: alpha(colors.text.secondary, 0.2), flexShrink: 0 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <ScheduleIcon sx={{ fontSize: 12, color: alpha(colors.blue.electric, 0.5) }} />
              <Typography sx={{ fontSize: '0.7rem', color: alpha(colors.text.secondary, 0.55), lineHeight: 1 }} noWrap>
                {details.dateTime}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {Array.from(groupedMatches.entries()).map(([date, dateMatches]) => (
        <Box key={date} sx={{ mb: 3 }}>
          {/* Date Group Header */}
          <Box
            sx={{
              position: 'relative',
              pl: 2.5,
              py: 0.75,
              mb: 1.25,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '3px',
                height: '70%',
                background: `linear-gradient(180deg, ${colors.blue.electric}, ${colors.blue.light})`,
                borderRadius: '2px',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography
                fontWeight={700}
                sx={{
                  fontSize: '0.95rem',
                  background: `linear-gradient(90deg, ${colors.text.primary} 60%, ${colors.blue.light})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {date}
              </Typography>
              <Chip
                label={`${dateMatches.length} match${dateMatches.length !== 1 ? 'es' : ''}`}
                size="small"
                sx={{
                  bgcolor: alpha(colors.blue.electric, 0.1),
                  color: colors.blue.light,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  height: 18,
                  border: `1px solid ${alpha(colors.blue.electric, 0.18)}`,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            </Box>
          </Box>

          <Box>{dateMatches.map(match => renderMatchCard(match))}</Box>
        </Box>
      ))}

      {matches.length === 0 && (
        <Card sx={{ ...cardSx }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <SportsCricket sx={{ fontSize: 56, color: colors.blue.electric, mb: 2, opacity: 0.6 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No Schedule Available</Typography>
            <Typography variant="body2" color="text.secondary">The match schedule hasn't been uploaded yet.</Typography>
          </CardContent>
        </Card>
      )}

      {/* ── Squad bottom sheet ── */}
      <Drawer
        anchor="bottom"
        open={!!selectedMatch}
        onClose={() => setSelectedMatch(null)}
        PaperProps={{
          sx: {
            borderRadius: '20px 20px 0 0',
            bgcolor: '#08111e',
            maxHeight: '80vh',
            backgroundImage: `linear-gradient(180deg, ${alpha(colors.blue.electric, 0.04)} 0%, transparent 120px)`,
          },
        }}
      >
        {selectedMatch && (() => {
          const squads = [selectedMatch.team1, selectedMatch.team2].map(team => {
            const teamNorm = normalize(team);
            const players = (playerPool ?? []).filter(p => {
              const pNorm = normalize(p.team);
              return pNorm === teamNorm || teamNorm.includes(pNorm) || pNorm.includes(teamNorm);
            });
            // Group by role
            const byRole: Record<string, typeof players> = {};
            players.forEach(p => {
              const r = p.role ?? 'other';
              if (!byRole[r]) byRole[r] = [];
              byRole[r].push(p);
            });
            return { team, players, byRole };
          });

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh' }}>
              {/* Drag handle */}
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.25, pb: 0.5, flexShrink: 0 }}>
                <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: alpha('#fff', 0.12) }} />
              </Box>

              {/* Header */}
              <Box sx={{ px: 2.5, pb: 1.25, pt: 0.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: alpha(colors.blue.electric, 0.7), mb: 0.25 }}>
                      Match {selectedMatch.matchNumber} · {selectedMatch.description}
                    </Typography>
                    <Typography fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2, color: colors.text.primary }}>
                      {selectedMatch.team1} <Box component="span" sx={{ color: alpha(colors.text.secondary, 0.4), fontWeight: 400, fontSize: '0.85rem' }}>vs</Box> {selectedMatch.team2}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                      <LocationOn sx={{ fontSize: 11, color: alpha(colors.text.secondary, 0.4) }} />
                      <Typography sx={{ fontSize: '0.7rem', color: alpha(colors.text.secondary, 0.45) }}>{selectedMatch.stadium}</Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => setSelectedMatch(null)} sx={{ color: alpha('#fff', 0.3), mt: -0.5, '&:hover': { color: alpha('#fff', 0.6) } }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ borderColor: alpha('#fff', 0.06), flexShrink: 0 }} />

              {/* Squad columns */}
              <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {squads.map(({ team, players, byRole }, idx) => {
                  const flag = cricketFlagEmoji(team);
                  const roleOrder = ['batsman', 'wicketkeeper', 'allrounder', 'bowler'];
                  return (
                    <Box
                      key={team}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        overflowY: 'auto',
                        borderRight: idx === 0 ? `1px solid ${alpha('#fff', 0.06)}` : 'none',
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      {/* Team header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        {flag && (
                          <Box sx={{ fontSize: '1.4rem', lineHeight: 1, flexShrink: 0 }}>{flag}</Box>
                        )}
                        <Box>
                          <Typography fontWeight={700} sx={{ fontSize: '0.8rem', lineHeight: 1.2, color: colors.text.primary }}>{team}</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.5) }}>{players.length} player{players.length !== 1 ? 's' : ''}</Typography>
                        </Box>
                      </Box>

                      {players.length === 0 ? (
                        <Typography sx={{ fontSize: '0.75rem', color: alpha(colors.text.secondary, 0.4), fontStyle: 'italic' }}>
                          No players in pool
                        </Typography>
                      ) : (
                        roleOrder.concat(
                          Object.keys(byRole).filter(r => !roleOrder.includes(r))
                        ).filter(r => byRole[r]?.length > 0).map(role => {
                          const meta = getRoleMeta(role);
                          return (
                            <Box key={role} sx={{ mb: 1.25 }}>
                              {/* Role label */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <Box sx={{ width: 2.5, height: 2.5, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: meta.color, opacity: 0.85 }}>
                                  {meta.label}
                                </Typography>
                              </Box>
                              {/* Players */}
                              {byRole[role].map(p => (
                                <Box
                                  key={p.playerId}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    py: '5px',
                                    px: 1,
                                    mb: 0.25,
                                    borderRadius: 1,
                                    bgcolor: alpha('#fff', 0.025),
                                    border: `1px solid ${alpha('#fff', 0.04)}`,
                                  }}
                                >
                                  {/* Initial avatar */}
                                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: alpha(meta.color, 0.15), border: `1px solid ${alpha(meta.color, 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: meta.color }}>{p.name.charAt(0).toUpperCase()}</Typography>
                                  </Box>
                                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: alpha(colors.text.primary, 0.9), lineHeight: 1.2 }} noWrap>{p.name}</Typography>
                                </Box>
                              ))}
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })()}
      </Drawer>
    </Box>
  );
};

export default MatchScheduleViewer;
